/* MIS MarineBond product page helpers. No app code, no tracking.
   1. <mb-sticky-atc>: phone-only sticky Add to cart bar. It appears when the main Add to cart button has scrolled
      above the screen, mirrors the live price and sold-out state, and clicks the theme's own button, so the
      selected variant and quantity are used and the cart drawer opens as usual.
   2. [data-mb-pack]: pack-size rows select that pack size in the theme's variant picker.
   3. <mb-product-recs>: loads "Complete your install" (complementary, falling back to related) and
      "You may also like" (related, minus what is already shown) through Shopify's recommendations endpoint. */

(function () {
  function sectionOf(el) {
    return el && el.dataset ? el.dataset.section : null;
  }

  /* ---------- 1. sticky add to cart ---------- */
  if (!customElements.get('mb-sticky-atc')) {
    class MbStickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.closest('quick-add-modal')) {
          this.remove();
          return;
        }
        this.sectionId = sectionOf(this);
        this.mainButton = document.getElementById('ProductSubmitButton-' + this.sectionId);
        this.button = this.querySelector('[data-mb-sticky-btn]');
        this.label = this.querySelector('[data-mb-sticky-label]');
        this.priceEl = this.querySelector('[data-mb-sticky-price]');
        if (!this.mainButton || !this.button) return;

        this.button.addEventListener('click', () => {
          if (this.mainButton.hasAttribute('disabled')) return;
          this.mainButton.click();
        });

        this.mq = window.matchMedia('(max-width: 749px)');
        // Shown whenever the theme's own Add to cart is off screen (below the first phone screen on load,
        // or scrolled past). Hidden while it is visible, so there is never a second button next to it.
        // S1 (final code check v5): the observer's first callback can arrive before the product form has laid out
        // (button 0x0), which flashed the bar for a frame. Every callback now only schedules a check; the check
        // measures the button itself two frames later and is skipped while the button has no real size.
        this.check = this.check.bind(this);
        this.observer = new IntersectionObserver(this.check, { threshold: [0, 0.9, 1] });
        this.observer.observe(this.mainButton);
        // re-check once the button gets (or changes) its size, and once the page has loaded
        if (window.ResizeObserver) {
          this.ro = new ResizeObserver(this.check);
          this.ro.observe(this.mainButton);
        }
        if (document.readyState !== 'complete') window.addEventListener('load', this.check, { once: true });

        this.sync = this.sync.bind(this);
        this.sync();
        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
          this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(this.sync, 0));
        }
        this.mo = new MutationObserver(this.sync);
        this.mo.observe(this.mainButton, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true });
      }

      check() {
        if (this.pending) return;
        this.pending = true;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            this.pending = false;
            if (!this.isConnected || !this.mainButton) return;
            const r = this.mainButton.getBoundingClientRect();
            if (r.width < 1 || r.height < 1) return; // not laid out yet: keep the current state
            const vh = window.innerHeight || document.documentElement.clientHeight;
            const seen = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
            this.toggle(seen / r.height < 0.9);
          })
        );
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.ro) this.ro.disconnect();
        if (this.mo) this.mo.disconnect();
        if (this.unsub) this.unsub();
      }

      toggle(show) {
        // The Vitals sticky bar is hidden by CSS on pages with this bar (mb-product.css). If it is still visible
        // (for example the app renamed its class), keep ours hidden so there are never two bars.
        const appBar = document.querySelector('.Vtl-StickyAddToCart, [class*="Vtl-StickyAddToCart"]');
        const appVisible = !!(appBar && appBar.offsetParent !== null && getComputedStyle(appBar).display !== 'none');
        const visible = show && this.mq.matches && !appVisible;
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        this.button.tabIndex = visible ? 0 : -1;
        document.body.classList.toggle('mb-sticky-atc-on', visible);
      }

      sync() {
        const disabled = this.mainButton.hasAttribute('disabled');
        this.button.toggleAttribute('disabled', disabled);
        const mainLabel = this.mainButton.querySelector('span');
        if (mainLabel && this.label) this.label.textContent = mainLabel.textContent.trim();
        const priceBox = document.getElementById('price-' + this.sectionId);
        if (priceBox && this.priceEl) {
          const sale = priceBox.querySelector('.price--on-sale .price__sale .price-item--sale');
          const regular = priceBox.querySelector('.price__regular .price-item--regular');
          const node = sale || regular;
          if (node) this.priceEl.textContent = node.textContent.trim();
        }
      }
    }
    customElements.define('mb-sticky-atc', MbStickyAtc);
  }

  /* ---------- 2. pack rows ---------- */
  function initPacks(root) {
    (root || document).querySelectorAll('[data-mb-pack]').forEach((pack) => {
      if (pack.dataset.mbReady) return;
      pack.dataset.mbReady = '1';
      const sectionId = pack.dataset.section;
      // PC1: the theme's radio names/values can carry a trailing newline ("Count-2\n"), so compare trimmed.
      const name = (pack.dataset.optionName + '-' + pack.dataset.optionPosition).trim();
      const norm = (v) => String(v == null ? '' : v).trim();
      const picker = () => document.getElementById('variant-selects-' + sectionId);

      const mark = () => {
        const p = picker();
        if (!p) return;
        const checked = Array.from(p.querySelectorAll('input[type="radio"]')).find(
          (i) => norm(i.name) === name && i.checked
        );
        const select = Array.from(p.querySelectorAll('select')).find((s) => norm(s.name) === norm('options[' + pack.dataset.optionName + ']'));
        const value = checked ? norm(checked.value) : select ? norm(select.value) : null;
        pack.querySelectorAll('[data-value]').forEach((row) => {
          row.setAttribute('aria-pressed', String(norm(row.dataset.value) === value));
          // sold out for the other chosen options (e.g. this colour): copy the theme's own state onto the pill
          const native = Array.from(p.querySelectorAll('input[type="radio"]')).find(
            (i) => norm(i.name) === name && norm(i.value) === norm(row.dataset.value)
          );
          const out = !!native && native.classList.contains('disabled');
          row.classList.toggle('is-soldout', out);
          const note = row.querySelector('.mb-pack__soldout');
          if (note) note.hidden = !out;
        });
      };

      pack.addEventListener('click', (event) => {
        const row = event.target.closest('[data-value]');
        if (!row) return;
        const p = picker();
        if (!p) return;
        const input = Array.from(p.querySelectorAll('input[type="radio"]')).find(
          (i) => norm(i.name) === name && norm(i.value) === norm(row.dataset.value)
        );
        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        pack.querySelectorAll('[data-value]').forEach((r) => r.setAttribute('aria-pressed', String(r === row)));
      });

      document.addEventListener('change', (event) => {
        const p = picker();
        if (p && p.contains(event.target)) {
          setTimeout(mark, 0);
          setTimeout(mark, 600); // after the theme re-renders the picker for the new variant
        }
      });
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        subscribe(PUB_SUB_EVENTS.variantChange, () => {
          setTimeout(mark, 0);
          setTimeout(mark, 600);
        });
      }
      mark();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initPacks());
  else initPacks();

  /* ---------- 3. recommendations ---------- */
  if (!customElements.get('mb-product-recs')) {
    class MbProductRecs extends HTMLElement {
      connectedCallback() {
        if (this.loaded) return;
        const start = () => {
          if (this.loaded) return;
          this.loaded = true;
          this.load();
        };
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            (entries) => {
              if (entries[0].isIntersecting) {
                this.io.disconnect();
                start();
              }
            },
            { rootMargin: '0px 0px 600px 0px' }
          );
          this.io.observe(this);
        } else start();
      }

      fetchList(intent, limit) {
        const url =
          this.dataset.url +
          '?product_id=' +
          encodeURIComponent(this.dataset.productId) +
          '&section_id=' +
          encodeURIComponent(this.dataset.sectionId) +
          '&limit=' +
          limit +
          '&intent=' +
          intent;
        return fetch(url)
          .then((r) => (r.ok ? r.text() : ''))
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            return Array.from(doc.querySelectorAll('[data-mb-recs-list] > li[data-pid]'));
          })
          .catch(() => []);
      }

      render(target, items) {
        const ul = document.createElement('ul');
        ul.className = 'mb-precs__grid';
        ul.setAttribute('role', 'list');
        items.forEach((li) => ul.appendChild(document.importNode(li, true)));
        target.innerHTML = '';
        target.appendChild(ul);
      }

      load() {
        const fbtLimit = parseInt(this.dataset.fbtLimit || '3', 10);
        const relLimit = parseInt(this.dataset.relatedLimit || '4', 10);
        // Audit 2026-10-24 #2: on kit pages never a smaller kit, the applicator gun or 1:1 DIY tips; never 1:1 tips
        // next to 10:1 adhesive (kits, 50ML). Kit pages show the preferred add-ons (data-pref) first.
        const RANK = { 'mounting-starter-kit': 1, 'adhesive-mounting-starter-kit': 2, 'adhesive-mounting-master-kit-xxl': 3 };
        const GUN = 'methacrylate-50ml-10-1-mix-round-flange';
        const TIPS_1_1 = 'static-mixer-for-2-part-methacrylate-25ml-50ml-1-1-mix-b-system-s-system-twist-lock';
        const page = this.dataset.productHandle || '';
        const pageRank = RANK[page] || 0;
        const tenToOne = pageRank > 0 || page === 'gray-fast-cure-2-part-methacrylate-adhesive';
        const ok = (li) => {
          const h = li.dataset.handle || '';
          if (h === page) return false;
          if (pageRank && RANK[h] && RANK[h] < pageRank) return false;
          if (pageRank && h === GUN) return false;
          if (tenToOne && h === TIPS_1_1) return false;
          return true;
        };
        const uniq = (list) => {
          const seen = new Set();
          return list.filter((li) => (seen.has(li.dataset.pid) ? false : (seen.add(li.dataset.pid), true)));
        };
        Promise.all([this.fetchList('complementary', fbtLimit), this.fetchList('related', fbtLimit + relLimit)]).then(
          ([compAll, relatedAll]) => {
            const prefs = uniq([...compAll, ...relatedAll].filter((li) => li.hasAttribute('data-pref') && ok(li)));
            const comp = compAll.filter((li) => !li.hasAttribute('data-pref') && ok(li));
            const related = relatedAll.filter((li) => !li.hasAttribute('data-pref') && ok(li));
            let fbt = pageRank ? uniq([...prefs, ...comp]).slice(0, fbtLimit) : comp.slice(0, fbtLimit);
            if (fbt.length === 0) fbt = related.slice(0, Math.min(fbtLimit, related.length));
            const used = new Set(fbt.map((li) => li.dataset.pid));
            const rel = related.filter((li) => !used.has(li.dataset.pid)).slice(0, relLimit);

            const fbtBox = this.querySelector('[data-mb-fbt]');
            const relBox = this.querySelector('[data-mb-related]');
            if (fbt.length && fbtBox) {
              this.render(fbtBox.querySelector('[data-mb-fbt-list]'), fbt);
              fbtBox.hidden = false;
            }
            if (rel.length >= 2 && relBox) {
              this.render(relBox.querySelector('[data-mb-related-list]'), rel);
              relBox.hidden = false;
            }
            if (!fbt.length && rel.length < 2) this.closest('.shopify-section')?.classList.add('mb-precs--empty');
          }
        );
      }
    }
    customElements.define('mb-product-recs', MbProductRecs);
  }

  /* ---------- Buy it now (Ryan, 2026-10-24) ----------
     snippets/buy-buttons.liquid [data-mb-buy-now]: adds this product (+ any free gifts, tagged) to the existing cart
     with /cart/add.js, then goes to /checkout (Ryan, 2026-10-24). Never opens the drawer or the Mix & Match nudge. Mirrors the Add to
     cart button's disabled state (sold out / unavailable) when the variant changes. */
  const mbBuyNowInit = (btn) => {
    if (btn.dataset.mbBound) return;
    btn.dataset.mbBound = '1';
    const productForm = btn.closest('product-form');
    const form = btn.closest('form');
    const atc = form && form.querySelector('[name="add"]');
    if (atc) {
      const sync = () => {
        if (btn.classList.contains('loading')) return;
        btn.disabled = atc.hasAttribute('disabled') && !atc.classList.contains('loading');
      };
      new MutationObserver(sync).observe(atc, { attributes: true, attributeFilter: ['disabled', 'class'] });
    }
    const showError = (msg) => {
      const wrap = productForm && productForm.querySelector('.product-form__error-message-wrapper');
      const text = wrap && wrap.querySelector('.product-form__error-message');
      if (!wrap || !text) return;
      text.textContent = msg;
      wrap.toggleAttribute('hidden', !msg);
    };
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true' || !form) return;
      showError('');
      // Ryan (2026-10-24): add to the EXISTING cart (nothing is wiped), then go to checkout. The Master / XXL free
      // gift(s) go in the same request with the _free_gift property. Read the inputs directly (the gift picker is
      // separate from the form). NB: form.id would return the <input name="id">, so read the attribute.
      const formId = form.getAttribute('id');
      const idInput = form.querySelector('[name="id"]');
      const qtyInput = document.querySelector(`[name="quantity"][form="${formId}"]`) || form.querySelector('[name="quantity"]');
      const id = String((idInput && idInput.value) || '').trim();
      let qty = parseInt((qtyInput && qtyInput.value) || '1', 10);
      if (!(qty > 0)) qty = 1;
      if (!/^\d+$/.test(id)) {
        showError((window.cartStrings && window.cartStrings.error) || 'Error');
        return;
      }
      const items = [{ id: Number(id), quantity: qty }];
      const gp = window.mbGiftFor && window.mbGiftFor(formId);
      if (gp) gp.giftItems(qty).forEach((g) => items.push(g));
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');
      btn.querySelector('.loading__spinner')?.classList.remove('hidden');
      let ok = false;
      const ctrl = 'AbortController' in window ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(), 15000) : null;
      try {
        const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
          signal: ctrl ? ctrl.signal : undefined,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) {
          showError(data.description || data.message || (window.cartStrings && window.cartStrings.error) || 'Error');
        } else {
          ok = true;
          window.location.href = '/checkout';
        }
      } catch (err) {
        showError((window.cartStrings && window.cartStrings.error) || 'Error');
      } finally {
        if (timer) clearTimeout(timer);
        if (!ok) {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('loading');
          btn.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }
    });
    // back/forward cache: never come back to a spinning button
    window.addEventListener('pageshow', () => {
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('loading');
      btn.querySelector('.loading__spinner')?.classList.add('hidden');
    });
  };
  const mbBuyNowScan = () => document.querySelectorAll('[data-mb-buy-now]').forEach(mbBuyNowInit);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mbBuyNowScan);
  else mbBuyNowScan();
  // re-bind if the product section is re-rendered (variant change can swap the form area)
  let mbBuyNowQueued = false;
  new MutationObserver(() => {
    if (mbBuyNowQueued) return;
    mbBuyNowQueued = true;
    requestAnimationFrame(() => {
      mbBuyNowQueued = false;
      mbBuyNowScan();
    });
  }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });

  /* ---------- 5. Shopify delivery promise at the bottom of the buy box (batch review N1 + N3, 2026-10-24) ----------
     On products without the gift picker Shopify puts its "Arrives as soon as … Promise" box inside the accelerated
     checkout block, between "Buy with Shop" and "More payment options"; on the kits it lands at the end of the form.
     Either way it moves to the end of the buy box, below all buttons, the trust box, the TDS button and "Compare kits"
     (so those sit right under the trust box). Watched with a MutationObserver because Shopify renders it late. */
  const mbPromiseMove = () => {
    document.querySelectorAll('.mb-pmain .product__info-container').forEach((info) => {
      info.querySelectorAll('product-form delivery-promise-wc').forEach((dp) => {
        let node = dp;
        const parent = dp.parentElement;
        if (
          parent &&
          parent.tagName === 'DIV' &&
          parent.children.length === 1 &&
          !parent.matches('.shopify-payment-button, .product-form__buttons, .mb-buy')
        ) {
          node = parent;
        }
        node.classList.add('mb-promise');
        const sticky = info.querySelector(':scope > mb-sticky-atc');
        if (sticky) info.insertBefore(node, sticky);
        else info.appendChild(node);
      });
    });
  };
  let mbPromiseQueued = false;
  const mbPromiseObserver = new MutationObserver(() => {
    if (mbPromiseQueued) return;
    mbPromiseQueued = true;
    requestAnimationFrame(() => {
      mbPromiseQueued = false;
      mbPromiseMove();
    });
  });

  /* ---------- 6. Desktop: a buy box taller than the window still shows its bottom (batch review N3) ----------
     The buy box is sticky at top 24px. When it is taller than the window, its bottom (TDS, Compare kits) stayed out
     of view until the whole page section had scrolled by. A negative top lets it scroll until its bottom is 24px
     above the window edge, then stick there. */
  const mbFitSticky = () => {
    document.querySelectorAll('.mb-pmain .product__info-container.product__column-sticky').forEach((col) => {
      if (window.innerWidth < 750) {
        col.style.top = '';
        return;
      }
      const gap = 24;
      const top = Math.min(gap, window.innerHeight - col.offsetHeight - gap);
      col.style.top = top + 'px';
    });
  };

  const mbBuyBoxStart = () => {
    mbPromiseMove();
    mbFitSticky();
    const root = document.querySelector('.mb-pmain');
    if (root) mbPromiseObserver.observe(root, { childList: true, subtree: true });
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(mbFitSticky);
      document.querySelectorAll('.mb-pmain .product__info-container').forEach((col) => ro.observe(col));
    }
    window.addEventListener('resize', mbFitSticky);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mbBuyBoxStart);
  else mbBuyBoxStart();
})();
